// Chart utilities and data aggregation functions for health facility visualizations

// Critical supplies list
export const criticalSuppliesList = [
  {
    id: 'digital_respiratory_timers',
    name: 'Digital Respiratory Timers',
    icon: '⏱️',
  },
  { id: 'oral_rehydration_salts', name: 'ORS Sachets', icon: '💊' },
  { id: 'zinc_tablets', name: 'Zinc Tablets', icon: '💊' },
  {
    id: 'rapid_diagnostic_test_kits_for_malaria',
    name: 'Malaria RDT Kits',
    icon: '🔬',
  },
  {
    id: 'act_tablets_for_treatment_of_malaria',
    name: 'ACT Tablets',
    icon: '💊',
  },
  {
    id: 'amoxicillin_tablets_for_treatment_of_pneumonia',
    name: 'Amoxicillin',
    icon: '💊',
  },
  {
    id: 'digital_clinical_thermometers_for_measuring_fever',
    name: 'Digital Thermometers',
    icon: '🌡️',
  },
]

// Staff categories with display names
export const staffCategoriesList = [
  {
    id: 'medical_officers',
    name: 'Medical Officers',
    category: 'Medical Staff',
    icon: '👨‍⚕️',
  },
  {
    id: 'clinical_officers',
    name: 'Clinical Officers',
    category: 'Medical Staff',
    icon: '👩‍⚕️',
  },
  {
    id: 'nursing_officers',
    name: 'Nursing Officers',
    category: 'Nursing Staff',
    icon: '👩‍⚕️',
  },
  {
    id: 'enrolled_nurses',
    name: 'Enrolled Nurses',
    category: 'Nursing Staff',
    icon: '👨‍⚕️',
  },
  {
    id: 'comprehensive_enrolled_nurse',
    name: 'Comprehensive Enrolled Nurses',
    category: 'Nursing Staff',
    icon: '👩‍⚕️',
  },
  {
    id: 'enrolled_midwives',
    name: 'Enrolled Midwives',
    category: 'Nursing Staff',
    icon: '🤱',
  },
  {
    id: 'nursing_assistants',
    name: 'Nursing Assistants',
    category: 'Support Staff',
    icon: '👨‍⚕️',
  },
  {
    id: 'laboratory_technicians',
    name: 'Laboratory Technicians',
    category: 'Technical Staff',
    icon: '🔬',
  },
  {
    id: 'laboratory_assistants',
    name: 'Laboratory Assistants',
    category: 'Technical Staff',
    icon: '🧪',
  },
  {
    id: 'health_information_assistants',
    name: 'Health Information Assistants',
    category: 'Support Staff',
    icon: '📊',
  },
  {
    id: 'vhts_chws',
    name: 'VHTs/CHWs',
    category: 'Community Health',
    icon: '🏘️',
  },
]

// Professional facility level color mapping aligned with design system
export const facilityLevelColors = {
  HCII: '#10b981', // Emerald 500
  HCIII: '#0ea5e9', // Sky 500
  HCIV: '#f59e0b', // Amber 500
  Hospital: '#ef4444', // Red 500
  Clinic: '#8b5cf6', // Violet 500
}

// Enhanced color palettes for charts
export const chartColorPalettes = {
  primary: [
    '#ef233c',
    '#c10e66',
    '#7f2a74',
    '#3b2f65',
    '#0b2440',
    '#10b981',
    '#f59e0b',
    '#8b5cf6',
    '#06b6d4',
    '#f97316',
    '#84cc16',
    '#ec4899',
    '#6b7280',
  ],
  facility: ['#ef233c', '#c10e66', '#7f2a74', '#3b2f65', '#0b2440'],
  status: [
    '#10b981', // Success
    '#f59e0b', // Warning
    '#ef233c', // Danger - updated to brand red
  ],
  heatmap: [
    '#cd2b2a', // Red
    '#3b8c62', // Green
    '#f7a838', // Yellow
  ],
  gradient: {
    primary: {
      start: '#ef233c',
      end: '#c10e66',
    },
    success: {
      start: '#10b981',
      end: '#059669',
    },
    warning: {
      start: '#f59e0b',
      end: '#d97706',
    },
  },
}

// Service list with display names
export const servicesList = [
  { id: 'immunization_services', name: 'Immunization Services', icon: '💉' },
  {
    id: 'maternal_and_child_health_services',
    name: 'Maternal & Child Health',
    icon: '👶',
  },
  { id: 'hivaids_and_tb_care', name: 'HIV/AIDS & TB Care', icon: '🏥' },
  {
    id: 'basic_diagnostic_services',
    name: 'Basic Diagnostic Services',
    icon: '🔬',
  },
  { id: 'counselling', name: 'Counselling', icon: '💬' },
  { id: 'health_education', name: 'Health Education', icon: '📚' },
  { id: 'family_planning_services', name: 'Family Planning', icon: '👪' },
  { id: 'advanced_surgical_procedures', name: 'Advanced Surgery', icon: '⚕️' },
  { id: 'outpatient_services', name: 'Outpatient Services', icon: '🚪' },
]

// Infrastructure list
export const infrastructureList = [
  { id: 'functional_backup_power', name: 'Backup Power' },
  { id: 'laboratory', name: 'Laboratory' },
  { id: 'treatment_room', name: 'Treatment Room' },
  { id: 'consulting_room', name: 'Consulting Room' },
  { id: 'delivery_room', name: 'Delivery Room' },
  { id: 'staff_accommodation', name: 'Staff Housing' },
  { id: 'waste_disposal_incinerator', name: 'Waste Disposal' },
]

// Calculate facility level distribution
export function calculateFacilityLevelDistribution(facilities) {
  const distribution = {}

  facilities.forEach(facility => {
    const level = facility.level || 'Unknown'
    distribution[level] = (distribution[level] || 0) + 1
  })

  return Object.entries(distribution).map(([name, value]) => ({
    name,
    value,
    itemStyle: { color: facilityLevelColors[name] || '#6b7280' },
  }))
}

// Calculate service coverage percentages
export function calculateServiceCoverage(facilities) {
  if (facilities.length === 0) return []

  return servicesList
    .map(service => {
      const facilitiesWithService = facilities.filter(
        facility => facility[service.id] === 'Yes'
      ).length

      const percentage = Math.round(
        (facilitiesWithService / facilities.length) * 100
      )

      // Color coding based on coverage
      let color = chartColorPalettes.heatmap[0] // Red for <50%
      if (percentage >= 80)
        color = chartColorPalettes.heatmap[1] // Green for >80%
      else if (percentage >= 50) color = chartColorPalettes.heatmap[2] // Yellow for 50-80%

      return {
        name: service.name,
        percentage,
        count: facilitiesWithService,
        total: facilities.length,
        color,
        icon: service.icon,
      }
    })
    .sort((a, b) => b.percentage - a.percentage) // Sort by coverage descending
}

// Calculate infrastructure readiness score
export function calculateInfrastructureReadiness(facilities) {
  if (facilities.length === 0) return []

  return infrastructureList.map(item => {
    const facilitiesWithItem = facilities.filter(
      facility => facility[item.id] === 'Yes'
    ).length

    const percentage = Math.round(
      (facilitiesWithItem / facilities.length) * 100
    )

    return {
      name: item.name,
      percentage,
      count: facilitiesWithItem,
      total: facilities.length,
    }
  })
}

// Calculate total staff count
export function calculateTotalStaff(facilities) {
  const staffFields = [
    'laboratory_assistants',
    'laboratory_technicians',
    'medical_officers',
    'nursing_assistants',
    'clinical_officers',
    'enrolled_midwives',
    'nursing_officers',
    'vhts_chws',
    'comprehensive_enrolled_nurse',
    'enrolled_nurses',
    'health_information_assistants',
  ]

  const staffTotals = {}
  let grandTotal = 0

  staffFields.forEach(field => {
    const total = facilities.reduce((sum, facility) => {
      const value = parseInt(facility[field]) || 0
      return sum + value
    }, 0)

    staffTotals[field] = total
    grandTotal += total
  })

  return {
    totals: staffTotals,
    grandTotal,
    averagePerFacility:
      facilities.length > 0 ? Math.round(grandTotal / facilities.length) : 0,
  }
}

// Generate location-specific insights
export function generateLocationInsights(facilities, locationName) {
  const insights = []

  if (facilities.length === 0) {
    return ['No facilities found in this location']
  }

  // Facility count insight
  insights.push(
    `${facilities.length} health facilities serving ${locationName}`
  )

  // Level distribution insight
  const levelDist = calculateFacilityLevelDistribution(facilities)
  const highestLevel = levelDist.reduce(
    (max, curr) => (curr.value > max.value ? curr : max),
    levelDist[0]
  )
  insights.push(
    `Most common facility type: ${highestLevel.name} (${highestLevel.value} facilities)`
  )

  // Service coverage insight
  const serviceCoverage = calculateServiceCoverage(facilities)
  const bestService = serviceCoverage[0]
  const worstService = serviceCoverage[serviceCoverage.length - 1]

  if (bestService.percentage >= 80) {
    insights.push(
      `Strong coverage: ${bestService.name} (${bestService.percentage}%)`
    )
  }

  if (worstService.percentage < 50) {
    insights.push(
      `Service gap: ${worstService.name} (${worstService.percentage}%)`
    )
  }

  // Staff insight
  const staffData = calculateTotalStaff(facilities)
  insights.push(
    `${staffData.grandTotal} total healthcare workers (avg ${staffData.averagePerFacility} per facility)`
  )

  return insights
}

// Enhanced chart configuration helpers with modern styling
export function createDonutChartOption(data, title, centerText) {
  return {
    title: {
      show: false,
      text: title,
      left: 'center',
      top: 20,
      textStyle: {
        fontSize: 18,
        fontWeight: '600',
        color: '#171717', // surface-900
        fontFamily: 'Poppins, system-ui, sans-serif',
      },
    },
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b}: {c} ({d}%)',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e5e7eb',
      borderWidth: 1,
      borderRadius: 8,
      textStyle: {
        fontFamily: 'Poppins, system-ui, sans-serif',
        fontSize: 13,
      },
      extraCssText: 'box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);',
    },
    graphic: {
      type: 'text',
      left: 'center',
      top: '50%',
      style: {
        text: centerText,
        textAlign: 'center',
        fill: '#0369a1', // primary-700
        fontSize: 18,
        fontWeight: '700',
        fontFamily: 'Poppins, system-ui, sans-serif',
      },
    },
    series: [
      {
        name: title,
        type: 'pie',
        radius: ['40%', '70%'],
        center: ['50%', '55%'],
        data: data,
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
      },
    ],
  }
}

export function createHorizontalBarChartOption(data, title) {
  return {
    title: {
      show: false,
      text: title,
      left: 'center',
      top: 20,
      textStyle: {
        fontSize: 18,
        fontWeight: '600',
        color: '#171717',
        fontFamily: 'Poppins, system-ui, sans-serif',
      },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
        shadowStyle: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
      formatter: function (params) {
        const item = params[0]
        const data = item.data
        return `${data.name}<br/>Coverage: ${data.percentage}%<br/>Facilities: ${data.count}/${data.total}`
      },
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e5e7eb',
      borderWidth: 1,
      borderRadius: 8,
      textStyle: {
        fontFamily: 'Poppins, system-ui, sans-serif',
        fontSize: 13,
      },
      extraCssText: 'box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);',
    },
    grid: {
      left: '10%',
      right: '10%',
      top: '15%',
      bottom: '10%',
      containLabel: true,
    },
    xAxis: {
      type: 'value',
      min: 0,
      max: 100,
      axisLabel: {
        formatter: '{value}%',
      },
      splitLine: {
        show: false,
      },
    },
    yAxis: {
      type: 'category',
      data: data.map(item => item.name),
      axisLabel: {
        fontSize: 12,
      },
      splitLine: {
        show: false,
      },
    },
    series: [
      {
        name: 'Coverage',
        type: 'bar',
        data: data.map(item => ({
          value: item.percentage,
          itemStyle: { color: item.color },
          ...item,
        })),
        label: {
          show: true,
          position: 'right',
          formatter: '{c}%',
          fontSize: 11,
        },
      },
    ],
  }
}

export function createRadarChartOption(data, title) {
  return {
    title: {
      show: false,
      text: title,
      left: 'center',
      top: 20,
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold',
      },
    },
    tooltip: {
      trigger: 'item',
      formatter: function (params) {
        const value = params.value
        const name = params.name
        return `${name}<br/>Infrastructure Score: ${value}%`
      },
    },
    radar: {
      center: ['50%', '55%'],
      radius: '65%',
      indicator: data.map(item => ({
        name: item.name,
        max: 100,
      })),
      axisLabel: {
        show: true,
        fontSize: 10,
        color: '#6b7280',
        formatter: '{value}%',
      },
      splitLine: {
        lineStyle: {
          color: '#e5e7eb',
        },
      },
    },
    series: [
      {
        name: title,
        type: 'radar',
        data: [
          {
            value: data.map(item => item.percentage),
            name: 'Infrastructure Coverage',
            itemStyle: {
              color: '#3b82f6',
            },
            areaStyle: {
              color: 'rgba(59, 130, 246, 0.3)',
            },
          },
        ],
      },
    ],
  }
}

// Calculate critical supply chain status
export function calculateSupplyChainStatus(facilities) {
  if (facilities.length === 0) return []

  return criticalSuppliesList
    .map(supply => {
      const facilitiesWithSupply = facilities.filter(
        facility => facility[supply.id] === 'Yes'
      ).length

      const percentage = Math.round(
        (facilitiesWithSupply / facilities.length) * 100
      )

      // Traffic light status
      let status = 'critical' // Red for <30%
      let statusColor = chartColorPalettes.heatmap[0] // Red for low coverage
      let statusText = 'Critical'

      if (percentage >= 70) {
        status = 'good'
        statusColor = chartColorPalettes.heatmap[1]
        statusText = 'Good'
      } else if (percentage >= 40) {
        status = 'warning'
        statusColor = chartColorPalettes.heatmap[2]
        statusText = 'Needs Attention'
      }

      return {
        name: supply.name,
        icon: supply.icon,
        percentage,
        count: facilitiesWithSupply,
        total: facilities.length,
        status,
        statusColor,
        statusText,
      }
    })
    .sort((a, b) => b.percentage - a.percentage)
}

// Calculate staffing analysis by category
export function calculateStaffingAnalysis(facilities) {
  if (facilities.length === 0) return { categories: [], totals: [], chart: [] }

  // Group staff by category
  const categoryTotals = {}
  const staffTotals = {}

  staffCategoriesList.forEach(staff => {
    const total = facilities.reduce((sum, facility) => {
      const value = parseInt(facility[staff.id]) || 0
      return sum + value
    }, 0)

    staffTotals[staff.id] = total

    if (!categoryTotals[staff.category]) {
      categoryTotals[staff.category] = 0
    }
    categoryTotals[staff.category] += total
  })

  // Prepare data for stacked bar chart
  const categories = Object.keys(categoryTotals)
  const chartData = []

  // Create series data for each staff type within categories
  categories.forEach(category => {
    const categoryStaff = staffCategoriesList.filter(
      s => s.category === category
    )
    categoryStaff.forEach(staff => {
      chartData.push({
        name: staff.name,
        category: category,
        value: staffTotals[staff.id],
        icon: staff.icon,
      })
    })
  })

  return {
    categories: Object.entries(categoryTotals).map(([name, value]) => ({
      name,
      value,
      percentage: Math.round(
        (value / Object.values(categoryTotals).reduce((a, b) => a + b, 0)) * 100
      ),
    })),
    totals: staffTotals,
    chart: chartData,
    grandTotal: Object.values(staffTotals).reduce((a, b) => a + b, 0),
  }
}

// Create stacked bar chart for staffing analysis
export function createStackedBarChartOption(data, title) {
  // Group data by category for stacked bars
  const categories = [...new Set(data.chart.map(item => item.category))]
  const staffNames = [...new Set(data.chart.map(item => item.name))]

  // Create color palette for different staff types
  const colors = chartColorPalettes.primary

  const series = staffNames.map((staffName, index) => {
    const staffData = categories.map(category => {
      const item = data.chart.find(
        d => d.name === staffName && d.category === category
      )
      return item ? item.value : 0
    })

    return {
      name: staffName,
      type: 'bar',
      stack: 'total',
      data: staffData,
      itemStyle: {
        color: colors[index % colors.length],
      },
    }
  })

  return {
    title: {
      show: false,
      text: title,
      left: 'center',
      top: 20,
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold',
      },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      formatter: function (params) {
        let result = `${params[0].name}<br/>`
        let total = 0
        params.forEach(param => {
          if (param.value > 0) {
            result += `${param.seriesName}: ${param.value}<br/>`
            total += param.value
          }
        })
        result += `<strong>Total: ${total}</strong>`
        return result
      },
    },
    legend: {
      type: 'scroll',
      orient: 'horizontal',
      top: 0,
      left: 'center',
      width: '80%',
    },
    grid: {
      left: '10%',
      right: '10%',
      top: '17%',
      bottom: '5%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: categories,
      axisLabel: {
        fontSize: 12,
        interval: 0,
        rotate: categories.length > 3 ? 45 : 0,
      },
      splitLine: {
        show: false,
      },
    },
    yAxis: {
      type: 'value',
      name: 'Number of Staff',
      nameTextStyle: {
        fontSize: 12,
      },
      splitLine: {
        show: false,
      },
    },
    series: series,
  }
}

// District comparison chart for multi-district mode
export function createDistrictComparisonChartOption(districtData, title) {
  const districts = districtData.map(d => d.name)
  const facilityCounts = districtData.map(d => d.facilityCount)

  // Calculate average service coverage for each district
  const avgServiceCoverage = districtData.map(d => {
    const avg =
      d.serviceCoverage.reduce((sum, service) => sum + service.percentage, 0) /
      d.serviceCoverage.length
    return Math.round(avg)
  })

  return {
    title: {
      text: title,
      left: 'center',
      top: 20,
      textStyle: {
        fontSize: 18,
        fontWeight: '600',
        color: '#171717',
        fontFamily: 'Poppins, system-ui, sans-serif',
      },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        label: {
          backgroundColor: '#6a7985',
        },
      },
      formatter: function (params) {
        let result = `${params[0].axisValue}<br/>`
        params.forEach(param => {
          const unit =
            param.seriesName === 'Facility Count' ? ' facilities' : '% coverage'
          result += `${param.seriesName}: ${param.value}${unit}<br/>`
        })
        return result
      },
    },
    legend: {
      top: 50,
      left: 'center',
    },
    grid: {
      left: '3%',
      right: '4%',
      top: '25%',
      bottom: '3%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: districts,
      axisLabel: {
        fontSize: 12,
        interval: 0,
        rotate: districts.length > 5 ? 45 : 0,
      },
      splitLine: {
        show: false,
      },
    },
    yAxis: [
      {
        type: 'value',
        name: 'Facility Count',
        position: 'left',
        axisLabel: {
          formatter: '{value}',
        },
        splitLine: {
          show: false,
        },
      },
      {
        type: 'value',
        name: 'Service Coverage %',
        position: 'right',
        axisLabel: {
          formatter: '{value}%',
        },
        splitLine: {
          show: false,
        },
      },
    ],
    series: [
      {
        name: 'Facility Count',
        type: 'bar',
        yAxisIndex: 0,
        data: facilityCounts,
        itemStyle: {
          color: '#3b82f6',
        },
      },
      {
        name: 'Avg Service Coverage',
        type: 'line',
        yAxisIndex: 1,
        data: avgServiceCoverage,
        itemStyle: {
          color: '#10b981',
        },
        lineStyle: {
          width: 3,
        },
      },
    ],
  }
}
